import type { IdNameResultOfint } from "@/openapi/generated/models";

export type AvailableRoomOption = {
  classRoomId: number;
  name: string;
};

function parseRoomEntry(entry: unknown): AvailableRoomOption | null {
  if (!entry || typeof entry !== "object") return null;
  const record = entry as Record<string, unknown>;
  const id = Number(record.classRoomId ?? record.id);
  const name =
    typeof record.name === "string"
      ? record.name
      : typeof record.label === "string"
        ? record.label
        : null;
  if (!Number.isFinite(id) || id <= 0 || !name?.trim()) return null;
  return { classRoomId: id, name: name.trim() };
}

/** Defensive parser - OpenAPI may not match runtime shape yet. */
export function parseAvailableRoomsResponse(data: unknown): AvailableRoomOption[] {
  if (Array.isArray(data)) {
    return data.flatMap((entry) => {
      const room = parseRoomEntry(entry);
      return room ? [room] : [];
    });
  }

  if (!data || typeof data !== "object") return [];

  const record = data as Record<string, unknown>;
  const candidates = [
    record.availableRooms,
    record.classRooms,
    record.rooms,
    record.items,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const parsed = candidate.flatMap((entry) => {
        const room = parseRoomEntry(entry);
        return room ? [room] : [];
      });
      if (parsed.length > 0) return parsed;
    }
  }

  const singleRoom = parseRoomEntry(record.classRoom);
  return singleRoom ? [singleRoom] : [];
}

export function mapIdNameResultsToRoomOptions(
  rooms: IdNameResultOfint[] | null | undefined,
): AvailableRoomOption[] {
  if (!Array.isArray(rooms)) return [];
  return rooms.flatMap((room) => {
    const classRoomId = Number(room.id);
    const name = room.name?.trim();
    if (!Number.isFinite(classRoomId) || !name) return [];
    return [{ classRoomId, name }];
  });
}
